import { useState, useEffect } from 'react';
import {
  TrendingUp, DollarSign, BarChart2, RefreshCw,
  Sliders, Save, Loader2, Trash2, FolderOpen
} from 'lucide-react';
import { motion } from 'motion/react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Slider } from '../ui/slider';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { db } from '../../utils/firebase/client';
import { collection, getDocs, Timestamp } from 'firebase/firestore';
import { guardedUpdate, guardedDelete } from '../../utils/firebase/writeGuard';
import { getCompanyId } from '../../utils/tenantContext';
import { toast } from 'sonner';
import { useTheme } from '../../hooks/useTheme';

interface SavedScenario {
  id: string;
  name: string;
  oilPrice: number;
  gasPrice: number;
  opexModifier: number;
  capexInvestment: number;
  productionDecline: number;
  createdAt: Date;
  updatedAt: Date;
}

interface ScenarioResult {
  month: string;
  revenue: number;
  opex: number;
  net: number;
  production: number;
}

// Default values for reset
const DEFAULT_VALUES = {
  oilPrice: 75,
  gasPrice: 3.5,
  opexModifier: 0,
  capexInvestment: 500000,
  productionDecline: 12
};

interface ScenarioPlannerProps {
  companyId?: string;  // Passed from parent for reliable tenant context
}

export default function ScenarioPlanner({ companyId: propCompanyId }: ScenarioPlannerProps) {
  const { isDarkMode } = useTheme();
  // Scenario Parameters
  const [oilPrice, setOilPrice] = useState([DEFAULT_VALUES.oilPrice]);
  const [gasPrice, setGasPrice] = useState([DEFAULT_VALUES.gasPrice]);
  const [opexModifier, setOpexModifier] = useState([DEFAULT_VALUES.opexModifier]);
  const [capexInvestment, setCapexInvestment] = useState([DEFAULT_VALUES.capexInvestment]);
  const [productionDecline, setProductionDecline] = useState([DEFAULT_VALUES.productionDecline]);

  // UI State
  const [results, setResults] = useState<ScenarioResult[]>([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [savedScenarios, setSavedScenarios] = useState<SavedScenario[]>([]);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [showLoadDialog, setShowLoadDialog] = useState(false);
  const [scenarioName, setScenarioName] = useState('');
  const [currentScenarioId, setCurrentScenarioId] = useState<string | null>(null);

  // Use prop companyId first (reliable from parent user object), fallback to context
  const companyId = propCompanyId || getCompanyId();

  // Load saved scenarios from Firestore on mount
  useEffect(() => {
    if (!companyId || companyId === 'default') {
      console.warn('⚠️ ScenarioPlanner: No valid companyId available');
      setLoading(false);
      return;
    }

    const loadSavedScenarios = async () => {
      try {
        const scenariosRef = collection(db, `companies/${companyId}/scenarios`);
        const snapshot = await getDocs(scenariosRef);

        const scenarios: SavedScenario[] = [];
        snapshot.forEach(doc => {
          const data = doc.data();
          scenarios.push({
            id: doc.id,
            name: data.name,
            oilPrice: data.oilPrice,
            gasPrice: data.gasPrice,
            opexModifier: data.opexModifier,
            capexInvestment: data.capexInvestment,
            productionDecline: data.productionDecline,
            createdAt: data.createdAt?.toDate() || new Date(),
            updatedAt: data.updatedAt?.toDate() || new Date()
          });
        });

        // Sort by most recent
        scenarios.sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime());
        setSavedScenarios(scenarios);

        // Load the most recent scenario if available
        if (scenarios.length > 0) {
          loadScenario(scenarios[0]);
        }
      } catch (error) {
        console.error('Failed to load scenarios:', error);
      } finally {
        setLoading(false);
      }
    };

    loadSavedScenarios();
  }, [companyId]);

  // Simulation Logic - runs whenever parameters change
  useEffect(() => {
    const data = [];
    let currentProduction = 1500; // bbl/day start
    const monthlyDecline = productionDecline[0] / 12 / 100;

    for (let month = 1; month <= 12; month++) {
      // Apply decline
      currentProduction = currentProduction * (1 - monthlyDecline);

      // Calculate Revenue
      const revenue = (currentProduction * 30 * oilPrice[0]) + (currentProduction * 30 * 2 * gasPrice[0]);

      // Calculate OpEx
      const baseOpex = 450000;
      const variableOpex = currentProduction * 30 * 8; // $8/bbl lifting cost
      const totalOpex = (baseOpex + variableOpex) * (1 + (opexModifier[0] / 100));

      // CapEx (One time in month 1)
      const capex = month === 1 ? capexInvestment[0] : 0;

      const netCashflow = revenue - totalOpex - capex;

      data.push({
        month: `M${month}`,
        revenue: Math.round(revenue),
        opex: Math.round(totalOpex),
        net: Math.round(netCashflow),
        production: Math.round(currentProduction)
      });
    }
    setResults(data);
  }, [oilPrice, gasPrice, opexModifier, capexInvestment, productionDecline]);

  // Load a scenario into the UI
  const loadScenario = (scenario: SavedScenario) => {
    setOilPrice([scenario.oilPrice]);
    setGasPrice([scenario.gasPrice]);
    setOpexModifier([scenario.opexModifier]);
    setCapexInvestment([scenario.capexInvestment]);
    setProductionDecline([scenario.productionDecline]);
    setCurrentScenarioId(scenario.id);
    setScenarioName(scenario.name);
    setShowLoadDialog(false);
    toast.success(`Loaded scenario: ${scenario.name}`);
  };

  // Reset to default values AND save to database
  const handleReset = async () => {
    setOilPrice([DEFAULT_VALUES.oilPrice]);
    setGasPrice([DEFAULT_VALUES.gasPrice]);
    setOpexModifier([DEFAULT_VALUES.opexModifier]);
    setCapexInvestment([DEFAULT_VALUES.capexInvestment]);
    setProductionDecline([DEFAULT_VALUES.productionDecline]);
    setCurrentScenarioId(null);
    setScenarioName('');

    toast.success('Reset to default values');
  };

  // Save scenario to Firestore
  const handleSaveScenario = async () => {
    if (!companyId) {
      toast.error('Please log in to save scenarios');
      return;
    }

    if (!scenarioName.trim()) {
      toast.error('Please enter a scenario name');
      return;
    }

    setSaving(true);
    try {
      const scenarioId = currentScenarioId || `scenario_${Date.now()}`;

      const scenarioData = {
        name: scenarioName.trim(),
        oilPrice: oilPrice[0],
        gasPrice: gasPrice[0],
        opexModifier: opexModifier[0],
        capexInvestment: capexInvestment[0],
        productionDecline: productionDecline[0],
        createdAt: currentScenarioId ? undefined : Timestamp.now(), // Only set on create
        updatedAt: Timestamp.now()
      };

      await guardedUpdate(`companies/${companyId}/scenarios`, scenarioId, scenarioData);

      setCurrentScenarioId(scenarioId);
      setShowSaveDialog(false);

      // Refresh the list
      const newScenario: SavedScenario = {
        id: scenarioId,
        name: scenarioName.trim(),
        oilPrice: oilPrice[0],
        gasPrice: gasPrice[0],
        opexModifier: opexModifier[0],
        capexInvestment: capexInvestment[0],
        productionDecline: productionDecline[0],
        createdAt: new Date(),
        updatedAt: new Date()
      };

      setSavedScenarios(prev => {
        const existing = prev.findIndex(s => s.id === scenarioId);
        if (existing >= 0) {
          const updated = [...prev];
          updated[existing] = newScenario;
          return updated;
        }
        return [newScenario, ...prev];
      });

      toast.success(`Scenario "${scenarioName}" saved to database`);
    } catch (error) {
      console.error('Failed to save scenario:', error);
      toast.error('Failed to save scenario');
    } finally {
      setSaving(false);
    }
  };

  // Delete a scenario from Firestore
  const handleDeleteScenario = async (scenarioId: string) => {
    if (!companyId) return;

    try {
      await guardedDelete(`companies/${companyId}/scenarios`, scenarioId);
      setSavedScenarios(prev => prev.filter(s => s.id !== scenarioId));

      if (currentScenarioId === scenarioId) {
        handleReset();
      }

      toast.success('Scenario deleted');
    } catch (error) {
      console.error('Failed to delete scenario:', error);
      toast.error('Failed to delete scenario');
    }
  };

  const totalNet = results.reduce((sum, r) => sum + r.net, 0);
  const roi = capexInvestment[0] > 0 ? ((totalNet / capexInvestment[0]) * 100).toFixed(1) : '0';

  // Calculate break-even oil price
  const calculateBreakeven = () => {
    // Simplified calculation
    const totalCosts = results.reduce((sum, r) => sum + r.opex, 0) + capexInvestment[0];
    const totalProduction = results.reduce((sum, r) => sum + r.production * 30, 0);
    return totalProduction > 0 ? (totalCosts / totalProduction).toFixed(2) : '0';
  };

  if (loading) {
    return (
      <div className="h-96 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Sliders className="w-6 h-6 text-purple-600" />
            Scenario Simulator
          </h2>
          <p className="text-gray-500 dark:text-gray-400">
            Run "What-If" analysis on field economics
            {currentScenarioId && <span className="ml-2 text-purple-600 font-medium">• {scenarioName}</span>}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowLoadDialog(true)} disabled={savedScenarios.length === 0}>
            <FolderOpen className="w-4 h-4 mr-2" />
            Load ({savedScenarios.length})
          </Button>
          <Button variant="outline" onClick={handleReset}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Reset
          </Button>
          <Button
            className="bg-purple-600 hover:bg-purple-700 text-white"
            onClick={() => setShowSaveDialog(true)}
          >
            <Save className="w-4 h-4 mr-2" />
            Save Scenario
          </Button>
        </div>
      </div>

      {/* Save Dialog */}
      <Dialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
        <DialogContent className={isDarkMode ? 'bg-slate-900 border-slate-700' : ''}>
          <DialogHeader>
            <DialogTitle className={isDarkMode ? 'text-white' : ''}>Save Scenario</DialogTitle>
            <DialogDescription className={isDarkMode ? 'text-gray-400' : ''}>
              Save this scenario configuration to your database. You can load it later.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className={isDarkMode ? 'text-gray-300' : ''}>Scenario Name</Label>
              <Input
                placeholder="e.g., Optimistic 2025 Forecast"
                value={scenarioName}
                onChange={(e) => setScenarioName(e.target.value)}
                className={isDarkMode ? 'bg-slate-800 border-slate-600 text-white' : ''}
              />
            </div>
            <div className={`text-sm space-y-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              <p>This will save:</p>
              <ul className="list-disc list-inside text-xs">
                <li>Oil Price: ${oilPrice[0]}/bbl</li>
                <li>Gas Price: ${gasPrice[0]}/mcf</li>
                <li>OpEx Modifier: {opexModifier[0]}%</li>
                <li>CapEx Investment: ${(capexInvestment[0] / 1000).toFixed(0)}k</li>
                <li>Decline Rate: {productionDecline[0]}%</li>
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSaveDialog(false)}>Cancel</Button>
            <Button
              className="bg-purple-600 hover:bg-purple-700 text-white"
              onClick={handleSaveScenario}
              disabled={saving || !scenarioName.trim()}
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              {saving ? 'Saving...' : 'Save to Database'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Load Dialog */}
      <Dialog open={showLoadDialog} onOpenChange={setShowLoadDialog}>
        <DialogContent className={isDarkMode ? 'bg-slate-900 border-slate-700' : ''}>
          <DialogHeader>
            <DialogTitle className={isDarkMode ? 'text-white' : ''}>Load Saved Scenario</DialogTitle>
            <DialogDescription className={isDarkMode ? 'text-gray-400' : ''}>
              Select a previously saved scenario to load its parameters.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-[300px] overflow-y-auto py-4">
            {savedScenarios.length === 0 ? (
              <p className={`text-center py-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>No saved scenarios yet</p>
            ) : (
              savedScenarios.map((scenario) => (
                <div
                  key={scenario.id}
                  className={`p-3 border rounded-lg cursor-pointer flex justify-between items-center ${currentScenarioId === scenario.id 
                    ? isDarkMode ? 'border-purple-500 bg-purple-900/30' : 'border-purple-500 bg-purple-50'
                    : isDarkMode ? 'border-slate-700 hover:bg-slate-800' : 'border-gray-200 hover:bg-gray-50'
                  }`}
                  onClick={() => loadScenario(scenario)}
                >
                  <div>
                    <h4 className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{scenario.name}</h4>
                    <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Oil: ${scenario.oilPrice} • Gas: ${scenario.gasPrice} • CapEx: ${(scenario.capexInvestment / 1000).toFixed(0)}k
                    </p>
                    <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                      Updated: {scenario.updatedAt.toLocaleDateString()}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className={`text-red-500 hover:text-red-700 ${isDarkMode ? 'hover:bg-red-900/30' : 'hover:bg-red-50'}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteScenario(scenario.id);
                    }}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLoadDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column: Controls */}
        <div className="space-y-6">
          <Card className={isDarkMode ? 'bg-slate-900 border-slate-700' : ''}>
            <CardHeader>
              <CardTitle className={`text-sm font-bold uppercase ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Market Conditions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <div className="flex justify-between">
                  <label className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : ''}`}>Oil Price (WTI)</label>
                  <span className="font-bold text-blue-600">${oilPrice[0]}/bbl</span>
                </div>
                <Slider value={oilPrice} onValueChange={setOilPrice} min={30} max={150} step={1} className="py-2" />
              </div>

              <div className="space-y-2">
                <div className="flex justify-between">
                  <label className="text-sm font-medium">Gas Price (HH)</label>
                  <span className="font-bold text-blue-600">${gasPrice[0]}/mcf</span>
                </div>
                <Slider value={gasPrice} onValueChange={setGasPrice} min={1} max={10} step={0.1} className="py-2" />
              </div>
            </CardContent>
          </Card>

          <Card className={isDarkMode ? 'bg-slate-900 border-slate-700' : ''}>
            <CardHeader>
              <CardTitle className={`text-sm font-bold uppercase ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Operational Levers</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <div className="flex justify-between">
                  <label className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : ''}`}>OpEx Modifier</label>
                  <span className={`font-bold ${opexModifier[0] > 0 ? 'text-red-600' : 'text-green-600'}`}>
                    {opexModifier[0] > 0 ? '+' : ''}{opexModifier[0]}%
                  </span>
                </div>
                <Slider value={opexModifier} onValueChange={setOpexModifier} min={-30} max={30} step={1} className="py-2" />
                <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Adjust for inflation or efficiency gains</p>
              </div>

              <div className="space-y-2">
                <div className="flex justify-between">
                  <label className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : ''}`}>CapEx Investment</label>
                  <span className="font-bold text-purple-600">${(capexInvestment[0] / 1000).toFixed(0)}k</span>
                </div>
                <Slider value={capexInvestment} onValueChange={setCapexInvestment} min={0} max={5000000} step={50000} className="py-2" />
                <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>New drills, workovers, or equipment</p>
              </div>

              <div className="space-y-2">
                <div className="flex justify-between">
                  <label className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : ''}`}>Decline Rate (Annual)</label>
                  <span className="font-bold text-orange-600">{productionDecline[0]}%</span>
                </div>
                <Slider value={productionDecline} onValueChange={setProductionDecline} min={5} max={40} step={1} className="py-2" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Column: Results */}
        <div className="lg:col-span-2 space-y-6">
          {/* KPI Cards - Redesigned with Gradients */}
          <div className="grid grid-cols-3 gap-4">
            {/* Net Cashflow - Emerald Gradient */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.1, type: "spring" }}
              whileHover={{ scale: 1.05, y: -4 }}
              whileTap={{ scale: 0.98 }}
              className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-emerald-500 via-emerald-600 to-green-500 p-3.5"
            >
              <div className="text-center">
                <motion.div whileHover={{ rotate: 360 }} transition={{ duration: 0.6 }}>
                  <DollarSign className="w-5 h-5 mx-auto mb-1.5 text-white" strokeWidth={2.5} />
                </motion.div>
                <p className="text-[9px] font-bold text-white/80 uppercase mb-1">12-Month Net Cashflow</p>
                <h3 className="text-lg font-bold text-white">
                  ${(totalNet / 1000000).toFixed(2)}M
                </h3>
              </div>
            </motion.div>

            {/* ROI - Cyan Gradient */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2, type: "spring" }}
              whileHover={{ scale: 1.05, y: -4 }}
              whileTap={{ scale: 0.98 }}
              className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-cyan-500 via-cyan-600 to-blue-500 p-3.5"
            >
              <div className="text-center">
                <motion.div whileHover={{ rotate: 360 }} transition={{ duration: 0.6 }}>
                  <TrendingUp className="w-5 h-5 mx-auto mb-1.5 text-white" strokeWidth={2.5} />
                </motion.div>
                <p className="text-[9px] font-bold text-white/80 uppercase mb-1">Projected ROI</p>
                <h3 className="text-lg font-bold text-white">
                  {roi}%
                </h3>
              </div>
            </motion.div>

            {/* Break-even - Rose Gradient */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3, type: "spring" }}
              whileHover={{ scale: 1.05, y: -4 }}
              whileTap={{ scale: 0.98 }}
              className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-rose-500 via-rose-600 to-pink-500 p-3.5"
            >
              <div className="text-center">
                <motion.div whileHover={{ rotate: 360 }} transition={{ duration: 0.6 }}>
                  <BarChart2 className="w-5 h-5 mx-auto mb-1.5 text-white" strokeWidth={2.5} />
                </motion.div>
                <p className="text-[9px] font-bold text-white/80 uppercase mb-1">Break-even Oil Price</p>
                <h3 className="text-lg font-bold text-white">
                  ${calculateBreakeven()}
                </h3>
              </div>
            </motion.div>
          </div>

          {/* Main Chart */}
          <Card className={`h-[400px] ${isDarkMode ? 'bg-slate-900 border-slate-700' : ''}`}>
            <CardHeader>
              <CardTitle className={isDarkMode ? 'text-white' : ''}>Financial Projection (12 Months)</CardTitle>
            </CardHeader>
            <CardContent className="h-[340px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={results}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={isDarkMode ? '#334155' : '#e2e8f0'} />
                  <XAxis dataKey="month" stroke={isDarkMode ? '#94a3b8' : '#64748b'} />
                  <YAxis tickFormatter={(val) => `$${val / 1000}k`} stroke={isDarkMode ? '#94a3b8' : '#64748b'} />
                  <Tooltip
                    formatter={(value: number) => [`$${value.toLocaleString()}`, '']}
                    contentStyle={{ 
                      borderRadius: '8px',
                      backgroundColor: isDarkMode ? '#1e293b' : '#ffffff',
                      borderColor: isDarkMode ? '#334155' : '#e2e8f0',
                      color: isDarkMode ? '#f1f5f9' : '#1e293b'
                    }}
                  />
                  <Legend />
                  <Bar dataKey="revenue" name="Revenue" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="opex" name="OpEx" fill="#ef4444" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="net" name="Net Cashflow" fill="#10b981" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
